import React, { memo, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { GlassCard } from '@/components/shared/redesign';
import { GlassStatusBadge } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

interface ThreadModerationListItemProps {
  thread: CorkBoardThreadSummary;
  isMutating: boolean;
  onPin: (thread: CorkBoardThreadSummary) => void;
  onLock: (thread: CorkBoardThreadSummary) => void;
  onEditTags: (thread: CorkBoardThreadSummary) => void;
  onDelete: (thread: CorkBoardThreadSummary) => void;
  onViewResponses?: (thread: CorkBoardThreadSummary) => void;
  onEdit?: (thread: CorkBoardThreadSummary) => void;
  onClose?: (thread: CorkBoardThreadSummary) => void;
}

export const ThreadModerationListItem: React.FC<ThreadModerationListItemProps> = memo(
  ({ thread, isMutating, onPin, onLock, onEditTags, onDelete, onViewResponses, onEdit, onClose }) => {
    const { redesign, semantic } = useAdminColors();
    const linkColor = semantic.text.link;
    const isSurvey = thread.kind === 'survey';
    const isSurveyClosed = isSurvey && !!thread.survey?.isClosed;

    const tagDots = useMemo(
      () =>
        thread.tags.slice(0, 3).map((tag) => (
          <View
            key={tag.id}
            style={[styles.tagDot, { backgroundColor: tag.color || redesign.text.muted }]}
          />
        )),
      [thread.tags, redesign.text.muted],
    );

    const formattedDate = useMemo(() => {
      const date = new Date(thread.createdAt);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }, [thread.createdAt]);

    return (
      <GlassCard padding={0} style={styles.cardRoot}>
        <View style={styles.container}>
          <View style={styles.topRow}>
            <View style={styles.tagDots}>{tagDots}</View>
            <Text
              style={[styles.title, { color: redesign.text.primary }]}
              numberOfLines={1}
            >
              {thread.title}
            </Text>
            <View style={styles.badges}>
              {thread.isAnnouncement && (
                <GlassStatusBadge label="Announcement" variant="info" />
              )}
              {thread.isPinned && (
                <GlassStatusBadge label="Pinned" variant="warning" />
              )}
              {thread.isLocked && (
                <GlassStatusBadge label="Locked" variant="error" />
              )}
              {isSurveyClosed && <GlassStatusBadge label="Closed" variant="neutral" />}
            </View>
          </View>

          <View style={styles.meta}>
            <Text style={[styles.metaText, { color: redesign.text.secondary }]} numberOfLines={1}>
              {thread.creator.name}
            </Text>
            <Text style={[styles.metaSeparator, { color: redesign.text.subtle }]}>·</Text>
            <Text style={[styles.metaText, { color: redesign.text.secondary }]}>{formattedDate}</Text>
            <Text style={[styles.metaSeparator, { color: redesign.text.subtle }]}>·</Text>
            <Text style={[styles.metaText, { color: redesign.text.secondary }]}>
              {thread.replyCount} replies
            </Text>
            <Text style={[styles.metaSeparator, { color: redesign.text.subtle }]}>·</Text>
            <Text style={[styles.metaText, { color: redesign.text.secondary }]}>
              {thread.viewCount} views
            </Text>
          </View>

          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => onPin(thread)}
              disabled={isMutating}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel={`${thread.isPinned ? 'Unpin' : 'Pin'} thread: ${thread.title}`}
            >
              <Text
                style={[
                  styles.actionText,
                  { color: linkColor },
                  isMutating && styles.actionTextDisabled,
                ]}
              >
                {thread.isPinned ? 'Unpin' : 'Pin'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => onLock(thread)}
              disabled={isMutating}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel={`${thread.isLocked ? 'Unlock' : 'Lock'} thread: ${thread.title}`}
            >
              <Text
                style={[
                  styles.actionText,
                  { color: linkColor },
                  isMutating && styles.actionTextDisabled,
                ]}
              >
                {thread.isLocked ? 'Unlock' : 'Lock'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => onEditTags(thread)}
              disabled={isMutating}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel={`Edit tags for thread: ${thread.title}`}
            >
              <Text
                style={[
                  styles.actionText,
                  { color: linkColor },
                  isMutating && styles.actionTextDisabled,
                ]}
              >
                Tags
              </Text>
            </TouchableOpacity>
            {isSurvey && onViewResponses && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => onViewResponses(thread)}
                disabled={isMutating}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel={`View responses for survey: ${thread.title}`}
              >
                <Text
                  style={[
                    styles.actionText,
                    { color: linkColor },
                    isMutating && styles.actionTextDisabled,
                  ]}
                >
                  Responses ({thread.survey?.responseCount ?? 0})
                </Text>
              </TouchableOpacity>
            )}
            {isSurvey && onEdit && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => onEdit(thread)}
                disabled={isMutating}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel={`Edit survey: ${thread.title}`}
              >
                <Text
                  style={[
                    styles.actionText,
                    { color: linkColor },
                    isMutating && styles.actionTextDisabled,
                  ]}
                >
                  Edit
                </Text>
              </TouchableOpacity>
            )}
            {isSurvey && !isSurveyClosed && onClose && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => onClose(thread)}
                disabled={isMutating}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel={`Close survey: ${thread.title}`}
              >
                <Text
                  style={[
                    styles.actionText,
                    { color: linkColor },
                    isMutating && styles.actionTextDisabled,
                  ]}
                >
                  Close
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => onDelete(thread)}
              disabled={isMutating}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel={`Delete thread: ${thread.title}`}
            >
              <Text
                style={[
                  styles.actionText,
                  { color: semantic.status.error },
                  isMutating && styles.actionTextDisabled,
                ]}
              >
                Delete
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </GlassCard>
    );
  },
);

ThreadModerationListItem.displayName = 'ThreadModerationListItem';

const styles = StyleSheet.create({
  cardRoot: {
    marginBottom: 8,
  },
  container: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 8,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    // Reserve the status-badge height so toggling Pinned/Locked badges in/out
    // doesn't change the card height.
    minHeight: 28,
  },
  tagDots: {
    flexDirection: 'row',
    gap: 4,
  },
  tagDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  },
  badges: {
    flexDirection: 'row',
    gap: 4,
    flexShrink: 0,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  metaText: {
    fontSize: 12,
  },
  metaSeparator: {
    fontSize: 12,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  actionButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  actionText: {
    fontSize: 13,
    fontWeight: '500',
  },
  actionTextDisabled: {
    opacity: 0.4,
  },
});
